import { print } from "graphql";
import {
  CompanionLlmStreamDocument,
  CompanionLlmStreamSubscription,
  CompanionLlmStreamSubscriptionVariables,
} from "../graphql/generated/graphql";
import { getValidAccessToken, refreshAccessToken } from "../utils/authRefresh";

type CompanionLlmStreamEvent = NonNullable<CompanionLlmStreamSubscription["companionLlmStream"]>;

type StreamHandlers = {
  onChunk?: (event: CompanionLlmStreamEvent) => void;
  onComplete?: () => void;
  onError?: (error: Error) => void;
};

const GRAPHQL_SSE_ENDPOINT =
  typeof window === "undefined" ? "/api/graphql/stream" : `${window.location.origin}/api/graphql/stream`;

const COMPANION_LLM_STREAM_SUBSCRIPTION = print(CompanionLlmStreamDocument);

let activeController: AbortController | null = null;

const parseSseEvent = (eventChunk: string): { eventName: string; data?: string } => {
  const lines = eventChunk.split("\n");
  let eventName = "message";
  const dataLines: string[] = [];

  for (const line of lines) {
    if (line.startsWith("event:")) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trim());
    }
  }

  return {
    eventName,
    data: dataLines.length ? dataLines.join("\n") : undefined,
  };
};

export const cancelCompanionLlmStream = () => {
  if (activeController) {
    activeController.abort();
    activeController = null;
  }
};

export const startCompanionLlmStream = async (
  question: string,
  controller: AbortController,
  handlers: StreamHandlers = {},
  options?: Omit<CompanionLlmStreamSubscriptionVariables, "question">
) => {
  cancelCompanionLlmStream();
  activeController = controller;

  const chunkQueue: CompanionLlmStreamEvent[] = [];
  let streamEnded = false;
  let onCompleteCalled = false;
  const MAX_CHUNKS_PER_TICK = 1; // steady pacing to avoid bursts
  const TICK_MS = 12;
  let drainTimer: ReturnType<typeof setInterval> | null = null;

  const stopDrainTimer = () => {
    if (drainTimer) {
      clearInterval(drainTimer);
      drainTimer = null;
    }
  };

  const flushAllChunks = () => {
    while (chunkQueue.length) {
      const chunk = chunkQueue.shift();
      if (chunk) {
        handlers.onChunk?.(chunk);
      }
    }
  };

  const finalizeIfComplete = () => {
    if (streamEnded && !onCompleteCalled && chunkQueue.length === 0) {
      onCompleteCalled = true;
      handlers.onComplete?.();
      stopDrainTimer();
    }
  };

  const processChunkQueue = () => {
    if (chunkQueue.length === 0) {
      finalizeIfComplete();
      return;
    }

    const chunksToProcess = Math.min(MAX_CHUNKS_PER_TICK, chunkQueue.length);
    for (let i = 0; i < chunksToProcess; i++) {
      const chunk = chunkQueue.shift();
      if (chunk) {
        handlers.onChunk?.(chunk);
      }
    }

    finalizeIfComplete();
  };

  const enqueueChunk = (chunk: CompanionLlmStreamEvent) => {
    chunkQueue.push(chunk);
    if (!drainTimer) {
      drainTimer = setInterval(processChunkQueue, TICK_MS);
    }
  };

  try {
    const fetchWithAuth = async () => {
      let accessToken = await getValidAccessToken({ minTtlSeconds: 30 });
      if (!accessToken) {
        throw new Error("No access token available for Companion GraphQL SSE stream.");
      }

      let response = await fetch(GRAPHQL_SSE_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          operationName: "CompanionLlmStream",
          query: COMPANION_LLM_STREAM_SUBSCRIPTION,
          variables: { question, ...options },
        }),
        signal: controller.signal,
      });

      if (response.status === 401 || response.status === 403) {
        const refreshed = await refreshAccessToken();
        if (refreshed?.accessToken) {
          accessToken = refreshed.accessToken;
          response = await fetch(GRAPHQL_SSE_ENDPOINT, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "text/event-stream",
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
              operationName: "CompanionLlmStream",
              query: COMPANION_LLM_STREAM_SUBSCRIPTION,
              variables: { question, ...options },
            }),
            signal: controller.signal,
          });
        }
      }

      return response;
    };

    const response = await fetchWithAuth();

    if (!response.ok) {
      throw new Error("Unable to start Companion GraphQL SSE stream.");
    }

    if (!response.body) {
      throw new Error("Server provided no stream body.");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        streamEnded = true;
        flushAllChunks();
        finalizeIfComplete();
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      let boundary = buffer.indexOf("\n\n");
      while (boundary !== -1) {
        const eventChunk = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);

        const { eventName, data } = parseSseEvent(eventChunk);

        // GraphQL SSE keep-alive frames come through as comment-only "message" events (":\n\n")
        if (eventName === "message" && !data) {
          boundary = buffer.indexOf("\n\n");
          continue;
        }

        // GraphQL SSE uses "next" for data events, "complete" for completion, "error" for errors
        if (eventName === "complete") {
          streamEnded = true;
          flushAllChunks();
          finalizeIfComplete();
          cancelCompanionLlmStream();
          return;
        }

        if (eventName === "error") {
          try {
            const errorData = data ? JSON.parse(data) : {};
            const error = new Error(errorData.message || "Stream error");
            handlers.onError?.(error);
          } catch (err) {
            handlers.onError?.(err as Error);
          }
          return;
        }

        // Handle "next" events (data events in GraphQL SSE)
        if (eventName === "next" && data) {
          try {
            const parsed = JSON.parse(data);

            if (parsed?.errors?.length) {
              const combinedErrorMessage = parsed.errors
                .map((err: { message?: string }) => err.message)
                .filter(Boolean)
                .join(" | ");
              const error = new Error(combinedErrorMessage || "GraphQL stream error");
              console.error("[companionGraphqlSseClient] GraphQL errors in next event", {
                combinedErrorMessage,
                errorsCount: parsed.errors.length,
              });
              handlers.onError?.(error);
              boundary = buffer.indexOf("\n\n");
              continue;
            }
            const payload = parsed?.data?.companionLlmStream as CompanionLlmStreamEvent | undefined;
            if (payload) {
              // queue the chunk instead of processing immediately
              enqueueChunk(payload);
            } else {
              // console.warn("[companionGraphqlSseClient] No payload in next event", {
              //   parsed,
              //   dataKeys: parsed ? Object.keys(parsed) : [],
              //   dataDataKeys: parsed?.data ? Object.keys(parsed.data) : [],
              // });
            }
          } catch (err) {
            console.error("[companionGraphqlSseClient] Error parsing next event", {
              err,
              errorMessage: (err as Error).message,
              data: data.substring(0, 500),
            });
            handlers.onError?.(err as Error);
          }
        } else if (eventName !== "complete" && eventName !== "error") {
          /* console.log("[companionGraphqlSseClient] Unknown event type", {
            eventName,
            hasData: !!data,
            dataPreview: data?.substring(0, 200),
          });*/
        }

        boundary = buffer.indexOf("\n\n");
      }
    }
  } catch (error) {
    if ((error as DOMException)?.name === "AbortError") {
      return;
    }
    handlers.onError?.(error as Error);
    throw error;
  } finally {
    stopDrainTimer();
    if (activeController === controller) {
      activeController = null;
    }
  }
};
